import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, ChevronRight, Hash } from 'lucide-react';
import { resetPasswordWithCode } from '../../../core/services/passwordResetService';

const PREAUTH_SHELL =
  'qawaem-preauth-route flex min-h-screen w-full flex-col items-center overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900 px-4 py-8 sm:py-12';

const CARD_OUTER =
  'relative w-full overflow-hidden rounded-3xl border border-white/20 bg-white/10 shadow-xl shadow-black/25 backdrop-blur-lg';

const QAWAEM_FAVICON_SRC = encodeURI(
  `${process.env.PUBLIC_URL ?? ''}/qawaem/favicon.png`,
);

const INPUT_CLASS =
  'w-full rounded-xl border border-white/20 bg-white/5 py-3.5 pl-12 pr-4 text-white placeholder:text-white/35 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const presetEmail = location.state?.email || '';

  const [email, setEmail] = useState(presetEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const em = email.trim();
    const c = code.trim();

    if (!em) {
      setError(t('common.reset_password_enter_email'));
      return;
    }
    if (!/^\d{6}$/.test(c)) {
      setError(t('common.reset_password_code_invalid'));
      return;
    }
    if (password.length < 6) {
      setError(t('common.reset_password_min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('common.reset_password_mismatch'));
      return;
    }

    setLoading(true);
    try {
      const result = await resetPasswordWithCode({
        email: em,
        code: c,
        newPassword: password,
      });
      if (result.success) {
        try {
          const adminJson = localStorage.getItem('qawaem_admin_user');
          if (adminJson) {
            const admin = JSON.parse(adminJson);
            admin.password = password;
            localStorage.setItem('qawaem_admin_user', JSON.stringify(admin));
          }
        } catch (_) {
          /* ignore */
        }
        setDone(true);
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      } else {
        setError(result.error || t('common.reset_password_invalid_code'));
      }
    } catch (err) {
      setError(err?.message || t('common.reset_password_unexpected_error'));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={PREAUTH_SHELL}>
        <div className={`mx-auto w-full max-w-lg p-8 ${CARD_OUTER} text-center`}>
          <CheckCircle className="mx-auto mb-4 h-12 w-12 text-emerald-300" />
          <h1 className="text-xl font-bold text-emerald-100">{t('common.reset_password_success')}</h1>
          <p className="mt-2 text-white/60">{t('common.reset_password_redirect')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={PREAUTH_SHELL}>
      <div className={`mx-auto w-full max-w-lg p-6 sm:p-10 ${CARD_OUTER}`}>
        <div className="mb-8 text-center">
          <img src={QAWAEM_FAVICON_SRC} alt="Qawaem" className="mx-auto mb-5 h-14 w-14 object-contain" />
          <h1 className="text-2xl font-bold text-white">{t('common.reset_password_title')}</h1>
          <p className="mt-2 text-sm text-white/60">
            {t('common.reset_password_subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-white/70">{t('common.reset_password_email_label')}</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/35" />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('common.reset_password_email_placeholder')}
                autoComplete="email"
                required
                className={INPUT_CLASS}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-white/70">{t('common.reset_password_code_label')}</label>
            <div className="relative">
              <Hash className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/35" />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('common.reset_password_code_placeholder')}
                autoComplete="one-time-code"
                required
                className={`${INPUT_CLASS} font-mono text-lg tracking-[0.35em] pr-4`}
              />
            </div>
            <p className="mt-2 text-xs text-white/45">{t('common.reset_password_code_hint')}</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-white/70">{t('common.reset_password_new_label')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/35" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
                className={`${INPUT_CLASS} pr-12`}
                placeholder={t('common.reset_password_min_chars')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                aria-label={showPassword ? t('common.close') : t('common.view')}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-white/70">{t('common.reset_password_confirm_label')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/35" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={6}
                required
                className={INPUT_CLASS}
                placeholder={t('common.reset_password_confirm_placeholder')}
              />
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 pt-2">
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={loading ? {} : { scale: 1.03 }}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-3.5 font-semibold text-white shadow-lg disabled:opacity-55"
            >
              {loading ? t('common.reset_password_saving') : (
                <>
                  {t('common.save')}
                  <ChevronRight className="h-5 w-5" />
                </>
              )}
            </motion.button>
            <Link to="/forgot-password" className="text-sm text-white/50 hover:text-white/75">
              {t('common.reset_password_resend')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
